'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { mutate as swrMutate } from 'swr'

import { useApiRequest } from '@/hooks/use-api'
import { useDataReadiness } from '@/hooks/use-data-readiness'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import {
  buildSyncReadinessRows,
  rowVisualState,
  syncPillState,
  type AccountRowModel,
} from '@/lib/sync-readiness-rows'
import { useSyncWidgetStore } from '@/stores/sync-widget-store'
import {
  dataReadinessKey,
  readinessRetryKey,
} from '@/stores/sync-widget-store'

export interface SyncReadinessRetryResult {
  task_id: string
  status: 'queued' | 'cooldown'
}

/**
 * Brand-wide account sync state — the single source of truth behind both the
 * Dashboard header's "needs attention" indicator and the Sync Widget's pill.
 *
 * Reads the same SWR readiness cache as `useDashboardVacuum` (SWR dedupes the
 * request, so consuming both costs nothing extra) and the same live import
 * sessions the widget already tracks. Because both surfaces count rows from
 * this one hook, their numbers agree by construction.
 *
 * Scope is deliberately brand-wide and NOT narrowed by the dashboard's account
 * filter: "which account needs you to do something" is a brand-level health
 * question, and hiding a broken account because the user filtered it out of the
 * current report would hide exactly the thing that needs acting on. The
 * dashboard's own "is the data for this view complete" signals stay scoped to
 * the current selection — those live in `useDashboardVacuum`.
 */
export interface BrandSyncReadiness {
  /** Every account row, manifest-backed and/or covered by a live import. */
  rows: AccountRowModel[]
  attentionRows: AccountRowModel[]
  attentionCount: number
  syncingCount: number
  anyAttention: boolean
  /**
   * Work is genuinely in flight: a live import session, or an account the row
   * model itself classifies as `syncing`.
   *
   * This is deliberately the SAME predicate the pill reads. The widget opens
   * exactly when `syncPillState` has something to say, so it can never appear
   * with no work to report — the failure mode this whole hook exists to end.
   *
   * Deriving it independently is what keeps re-introducing that bug. The first
   * version tested `recent_data_state === 'unknown'`, which aggregates to
   * `complete` and never enters `syncingCount`, so the widget opened and
   * rendered "Syncing 0 account(s)…" forever. The second tested
   * `bootstrap_state !== 'completed'`, which is true for every account that
   * needs the user to act — a broken connection never finishes bootstrapping —
   * so the widget opened for an account the row model calls `attention`, found
   * no label to render, and fell back to "All accounts synced" while the header
   * was simultaneously flagging that same account in red. Observed on staging
   * 2026-08-18, brand `soku`, account "ChatGPT Ads: Soku".
   *
   * First-time syncs are unaffected: an account that has started but not
   * finished bootstrapping and has no issue aggregates to `syncing`
   * (`aggregateBootstrapStatus` in `lib/data-readiness.ts`), so it counts here.
   */
  hasWorkInFlight: boolean
  /** Accounts covered by a session that has not finished settling. */
  importingCount: number
  /** Any session still retained by the store — `active` or `reconciling`. */
  hasPendingSessions: boolean
  retryingAccountIds: ReadonlySet<number>
  acceptedRetryAccountIds: ReadonlySet<number>
  retryAccount: (
    account: AccountRowModel['readiness'],
  ) => Promise<SyncReadinessRetryResult>
}

export function useBrandSyncReadiness(): BrandSyncReadiness {
  const brand = useOptionalCurrentBrand()
  const { request } = useApiRequest()
  // Deliberately no `as_of`: that parameter belongs to the reporting snapshot
  // and is part of the SWR key, so passing it on one surface and omitting it on
  // the other would put the header and the widget on two different cache
  // entries — and the counts could differ again for a reason nobody would
  // think to look for. Account health is a non-report signal, which is exactly
  // the consumer class that omits `as_of` (web-frontend-architecture.md §4).
  const { data } = useDataReadiness()
  const allSessions = useSyncWidgetStore(state => state.sessions)
  const readinessRetries = useSyncWidgetStore(state => state.readinessRetries)
  const beginReadinessRetry = useSyncWidgetStore(
    state => state.beginReadinessRetry,
  )
  const acceptReadinessRetry = useSyncWidgetStore(
    state => state.acceptReadinessRetry,
  )
  const failReadinessRetry = useSyncWidgetStore(
    state => state.failReadinessRetry,
  )
  const reconcileReadinessRetries = useSyncWidgetStore(
    state => state.reconcileReadinessRetries,
  )

  // A retry ACK means work was accepted, not that readiness recovered. Keep
  // the row in its manifest-backed attention state while separately exposing
  // the in-flight retry until a later manifest no longer reports that issue.
  useEffect(() => {
    if (!data || !brand) return
    reconcileReadinessRetries(
      brand.brandId,
      new Set(
        data.accounts
          .filter(account => account.issue?.action === 'retry')
          .map(account => account.id),
      ),
    )
  }, [brand, data, reconcileReadinessRetries])

  const retryAccount = useCallback(
    async (
      account: AccountRowModel['readiness'],
    ): Promise<SyncReadinessRetryResult> => {
      if (!brand || !account) {
        throw new Error('Cannot retry readiness without a scoped account')
      }
      if (!beginReadinessRetry(brand.brandId, account.id)) {
        return { task_id: '', status: 'cooldown' }
      }
      try {
        const result = await request<SyncReadinessRetryResult>({
          url: `/api/dashboard/data-readiness/${account.id}/retry`,
          method: 'POST',
          query: { brand_id: brand.brandId },
        })
        acceptReadinessRetry(brand.brandId, account.id)
        await swrMutate(dataReadinessKey(brand.brandId))
        return result
      } catch (error) {
        failReadinessRetry(brand.brandId, account.id)
        throw error
      }
    },
    [
      acceptReadinessRetry,
      beginReadinessRetry,
      brand,
      failReadinessRetry,
      request,
    ],
  )

  return useMemo(() => {
    // Brand switches are client-side navigations and the session store
    // survives them, so only the active brand's sessions belong to this scope.
    const sessions = brand
      ? Object.fromEntries(
          Object.entries(allSessions).filter(
            ([, session]) =>
              session.brandId === brand.brandId && session.visible !== false,
          ),
        )
      : {}
    const readinessAccounts = data?.accounts ?? []
    const retryingAccountIds = new Set<number>()
    const acceptedRetryAccountIds = new Set<number>()
    if (brand) {
      for (const account of readinessAccounts) {
        const status =
          readinessRetries[readinessRetryKey(brand.brandId, account.id)]
        if (status === 'retrying') retryingAccountIds.add(account.id)
        if (status === 'accepted') acceptedRetryAccountIds.add(account.id)
      }
    }
    const rows = buildSyncReadinessRows(readinessAccounts, sessions)
    const attentionRows = rows.filter(
      row => rowVisualState(row) === 'attention',
    )
    const manifestSyncingCount = rows.filter(
      row => rowVisualState(row) === 'syncing',
    ).length
    const syncingCount = manifestSyncingCount + acceptedRetryAccountIds.size
    // Both session states mean "not finished": `reconciling` is a completed
    // import still waiting on allocation and cache settlement.
    const pendingSessions = Object.values(sessions)
    // "Importing" counts only accounts covered by a session; unrelated
    // background bootstraps must not inflate it.
    const importingCount = pendingSessions.reduce(
      (sum, session) => sum + Object.keys(session.accounts).length,
      0,
    )
    // Asking the pill itself, rather than deriving a second opinion about what
    // counts as activity. `idle` is exactly "the pill has no label", so the
    // widget's open gate and its contents can no longer disagree.
    const pill = syncPillState({
      hasPendingSessions: pendingSessions.length > 0,
      importingCount,
      syncingCount,
    })
    return {
      rows,
      attentionRows,
      attentionCount: attentionRows.length,
      syncingCount,
      anyAttention: attentionRows.length > 0,
      hasWorkInFlight: pill.kind !== 'idle',
      importingCount,
      hasPendingSessions: pendingSessions.length > 0,
      retryingAccountIds,
      acceptedRetryAccountIds,
      retryAccount,
    }
  }, [
    allSessions,
    brand,
    data,
    retryAccount,
    readinessRetries,
  ])
}
